import { useState, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";
import { toast } from "sonner";
import { Plus, X, ClipboardList } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useFormAssignments } from "@/hooks/use-form-assignments";
import { getForms } from "@/lib/forms-api";
import { useOrgId } from "@/lib/org-context";

interface FormAssignmentsProps {
  assignableType: "project" | "task";
  assignableId: string;
  isAdmin: boolean;
}

export function FormAssignments({
  assignableType,
  assignableId,
  isAdmin,
}: FormAssignmentsProps) {
  const { assignments, add, remove } = useFormAssignments(
    assignableType,
    assignableId
  );
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);

  const orgId = useOrgId();

  // Fetch published standard forms when popover opens; refine with debounced search
  const { data: formsData } = useQuery({
    queryKey: ["forms", orgId, { type: "standard", status: "published", isArchived: false, search: debouncedSearch || undefined }],
    queryFn: () =>
      getForms({
        type: "standard",
        status: "published",
        isArchived: false,
        search: debouncedSearch || undefined,
      }),
    enabled: !!orgId && open,
  });
  const serverForms = formsData?.data ?? [];

  const assignedFormIds = new Set(assignments.map((a) => a.formId));

  // Client-side filter for instant feedback while debounced query catches up
  const filteredForms = useMemo(() => {
    const available = serverForms.filter((f) => !assignedFormIds.has(f.id));
    if (!search) return available;
    const q = search.toLowerCase();
    return available.filter((f) => f.title.toLowerCase().includes(q));
  }, [serverForms, assignedFormIds, search]);

  async function handleAdd(formId: string) {
    setOpen(false);
    setSearch("");
    try {
      await add.mutateAsync(formId);
      toast.success("Form assigned");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to assign form");
    }
  }

  async function handleRemove(formId: string, assignmentId: string) {
    try {
      await remove.mutateAsync({ formId, assignmentId });
      toast.success("Form removed");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove form"
      );
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Forms</h3>
        {isAdmin && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="end">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search forms..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>No forms found.</CommandEmpty>
                  <CommandGroup>
                    {filteredForms.map((f) => (
                      <CommandItem
                        key={f.id}
                        value={f.id}
                        onSelect={() => handleAdd(f.id)}
                      >
                        {f.title}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {assignments.length > 0 ? (
        <div className="divide-y">
          {assignments.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between py-2"
            >
              <div className="flex items-center gap-2.5">
                <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">{a.formTitle ?? a.formId}</span>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleRemove(a.formId, a.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No forms assigned.</p>
      )}
    </div>
  );
}
